/**
 * Магазин: настройки витрины и заявки покупателей.
 *
 * Витрина — серверная сущность: адрес, включена ли, сколько камней выставлено.
 * Заявки приходят с публичной страницы; здесь их читают и закрывают.
 * Камни попадают в витрину из своей карточки: Магазин → «В каталог» / «В промо».
 */
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { apiFetch } from '../sync';
import { plural } from '@core';

interface Front {
  slug: string; enabled: boolean; title: string | null; url: string;
  inCatalog: number; promo: number; newOrders: number;
}

export function Storefront({ onBack, onOrders }: { onBack: () => void; onOrders: () => void }) {
  const [front, setFront] = useState<Front | null>(null);
  const [problem, setProblem] = useState('');
  const [qr, setQr] = useState('');
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    setProblem('');
    try {
      const res = await apiFetch('/api/storefront');
      if (!res.ok) throw new Error(String(res.status));
      setFront((await res.json()) as Front);
    } catch {
      setProblem('Витрина живёт на сервере — без связи её не настроить.');
    }
  }
  useEffect(() => { void load(); }, []);

  useEffect(() => {
    if (!front?.enabled) { setQr(''); return; }
    void QRCode.toDataURL(front.url, { width: 480, margin: 1 }).then(setQr).catch(() => setQr(''));
  }, [front?.enabled, front?.url]);

  async function toggle() {
    if (!front) return;
    setBusy(true);
    try {
      await apiFetch('/api/storefront', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !front.enabled }),
      });
      await load();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app tabbed">
      <div className="bar">
        <button className="crumb" onClick={onBack}>‹ Ещё</button>
        <h1 className="t">Магазин</h1>
        <p className="meta">Витрина для покупателей: карусель промо, каталог, заявки</p>
      </div>

      <div className="scroll">
        {problem && <div className="memo warn">{problem}</div>}

        {front && (
          <>
            <div className="states" style={{ marginTop: 4 }}>
              <button className="state-btn" aria-pressed={front.enabled} disabled={busy}
                      onClick={() => void toggle()}>
                {front.enabled ? 'Витрина открыта ✓' : 'Открыть витрину'}
              </button>
            </div>

            <div className="tally" style={{ paddingTop: 12 }}>
              <div className={front.inCatalog ? 'on' : ''}><b>{front.inCatalog}</b><span>в каталоге</span></div>
              <div className={front.promo ? 'on' : ''}><b>{front.promo}</b><span>в промо</span></div>
              <div className={front.newOrders ? 'on' : ''}><b>{front.newOrders}</b><span>новых заявок</span></div>
            </div>

            {front.enabled ? (
              <>
                <div className="memo" style={{ marginTop: 0 }}>
                  <b>{front.url.replace('https://', '')}</b>
                  <br />Покупатель видит фото и паспорт камней — без цен, статусов
                  и заметок. Выбирает изделие с рендером или «только камень»
                  и оставляет контакт.
                </div>
                {qr && (
                  <div className="qrbox">
                    <img src={qr} alt="QR витрины" />
                    <span>Покажите с экрана или напечатайте — витрина откроется без приложения</span>
                  </div>
                )}
                <div className="dock" style={{ position: 'static', padding: '12px 0 0' }}>
                  <button className="btn btn-secondary" onClick={onOrders}>
                    Заявки{front.newOrders ? ` · ${front.newOrders}` : ''}
                  </button>
                  <button className="btn btn-primary" onClick={() => {
                    void navigator.clipboard.writeText(front.url).then(() => setCopied(true)).catch(() => {});
                  }}>
                    {copied ? 'Скопирован' : 'Скопировать адрес'}
                  </button>
                </div>
              </>
            ) : (
              <div className="memo" style={{ marginTop: 0 }}>
                Пока витрина закрыта, адрес не работает. Камни добавляются в неё
                из своей карточки: раздел <b>Магазин → «В каталог»</b>, лучшие —
                <b> «В промо»</b>: они крутятся в карусели наверху.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── заявки ──────────────────────────────────────────────────────────────────

interface Order {
  id: string; stone_id: string; public_no: string | null; local_no: string | null;
  kind: string; jewelry_type: string | null; metal: string | null;
  buyer_name: string | null; buyer_contact: string; note: string | null;
  status: string; created_at: string;
}

const JT_RU: Record<string, string> = { ring: 'кольцо', pendant: 'кулон', earrings: 'серьги' };
const MT_RU: Record<string, string> = { yellow: 'жёлтое золото', white: 'белое золото', rose: 'розовое золото' };

export function Orders({ onBack, onOpenStone }: {
  onBack: () => void; onOpenStone: (id: string) => void;
}) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [problem, setProblem] = useState('');

  async function load() {
    setProblem('');
    try {
      const res = await apiFetch('/api/orders');
      if (!res.ok) throw new Error(String(res.status));
      setOrders(((await res.json()) as { orders: Order[] }).orders);
    } catch {
      setProblem('Заявки живут на сервере — без связи их не показать.');
    }
  }
  useEffect(() => { void load(); }, []);

  async function done(id: string) {
    await apiFetch(`/api/orders/${id}/done`, { method: 'POST' });
    await load();
  }

  const what = (o: Order) => o.kind === 'jewelry'
    ? `${JT_RU[o.jewelry_type ?? ''] ?? 'изделие'}, ${MT_RU[o.metal ?? ''] ?? ''}`.replace(/, $/, '')
    : 'только камень';
  const fmt = (d: string) => new Date(d).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="app tabbed">
      <div className="bar">
        <button className="crumb" onClick={onBack}>‹ Назад</button>
        <h1 className="t">Заявки</h1>
        <p className="meta">
          {orders.filter((o) => o.status === 'new').length} {plural(orders.filter((o) => o.status === 'new').length, 'новая', 'новые', 'новых')} · всего {orders.length}
        </p>
      </div>

      <div className="scroll">
        {problem && <div className="memo warn">{problem}</div>}

        {!problem && orders.length === 0 && (
          <div className="blankslate">
            <b>Заявок пока нет</b>
            Они появятся, когда покупатель выберет камень на витрине
            и оставит контакт.
          </div>
        )}

        {orders.map((o) => (
          <div className="memo" key={o.id}
               style={{ marginTop: 10, opacity: o.status === 'done' ? 0.55 : 1 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <b style={{ fontSize: 17.5 }}>{o.buyer_name || 'Покупатель'}</b>
              <span className={`src ${o.status === 'new' ? 'src-claimed' : 'src-certified'}`}>
                {o.status === 'new' ? 'новая' : 'обработана'}
              </span>
              <span style={{ color: 'var(--ink-3)', fontSize: 13.5 }}>{fmt(o.created_at)}</span>
            </div>
            <div style={{ marginTop: 6 }}>
              <button className="crumb" style={{ padding: 0 }} onClick={() => onOpenStone(o.stone_id)}>
                {o.public_no ?? o.local_no ?? o.stone_id} ›
              </button>
              {' · '}{what(o)}
            </div>
            <div style={{ fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>{o.buyer_contact}</div>
            {o.note && <div style={{ marginTop: 4, color: 'var(--ink-2)' }}><q>{o.note}</q></div>}
            {o.status === 'new' && (
              <div className="states" style={{ marginTop: 10 }}>
                <button className="state-btn" onClick={() => void done(o.id)}>Обработана ✓</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
